import { NgClass } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, inject, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { faEdit, faEraser, faFilter, faMagnifyingGlass, faShareNodes, faTrash } from '@fortawesome/free-solid-svg-icons';
import { NgxMatDatepickerInput, NgxMatDatetimepicker } from '@ngxmc/datetime-picker';
import { takeUntil } from 'rxjs';
import { FolderPropertyCodes } from 'src/app/core/constantes/folder-property-codes';
import { AttributeFilterBaseDirective } from 'src/app/core/directives/attribute-filter-base.directive';
import { FoldersListFilterHandler } from 'src/app/core/folders-list-filter-handler';
import { PropertyType } from 'src/app/models/enums/property-type';
import { IFilterable } from 'src/app/models/interfaces/filterable.interface';
import { PredefinedFilter } from 'src/app/models/predefined-filter';
import { AttributeDefinition } from 'src/app/models/server/attribute-definition';
import { ColumnDefinition } from 'src/app/models/server/column-definition';
import { Colors } from 'src/app/models/server/enums/colors';
import { AttributeDefinitionsService } from 'src/app/services/http/attribute-definitions.service';
import { ConnectedUsersStateService } from 'src/app/services/state/connected-users-state.service';
import { FolderSearchStateService } from 'src/app/services/state/folder-search-state.service';
import { FormFocusDirective } from '../../../../core/directives/form-focus.directive';
import { AvmAttributeFilterSubstringPipe } from '../../../../core/pipes/avm-attribute-filter-substring.pipe';
import { AvmColorPipe } from '../../../../core/pipes/avm-color.pipe';
import { AvmDateAndTimePipe } from '../../../../core/pipes/avm-date-and-time.pipe';
import { AvmDatePipe } from '../../../../core/pipes/avm-date.pipe';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-followed-folders-search-bar',
    templateUrl: './followed-folders-search-bar.component.html',
    styleUrls: ['./followed-folders-search-bar.component.scss'],
    imports: [
        FormsModule,
        FormFocusDirective,
        ReactiveFormsModule,
        MatFormFieldModule,
        FaIconComponent,
        MatInputModule,
        MatButtonModule,
        MatTooltipModule,
        MatMenuModule,
        MatCheckboxModule,
        NgClass,
        MatChipsModule,
        MatIconModule,
        MatDatepickerModule,
        NgxMatDatepickerInput,
        NgxMatDatetimepicker,
        MatListModule,
        AvmDatePipe,
        AvmDateAndTimePipe,
        AvmColorPipe,
        AvmAttributeFilterSubstringPipe,
    ],
})
export class FollowedFoldersSearchBarComponent extends AttributeFilterBaseDirective implements OnInit, OnDestroy, AfterViewInit, IFilterable {
    private attributeDefinitionsService = inject(AttributeDefinitionsService);

    /**
     * Référence des icones utilisées dans le template
     */
    public resetFilterIcon = faEraser;
    public filterIcon = faFilter;
    public faMagnifyingGlass = faMagnifyingGlass;
    public editIcon = faEdit;
    public deleteIcon = faTrash;
    public shareIcon = faShareNodes;

    /**
     * Références des Enums utilisées dans le template.
     */
    public Colors = Colors;
    public Type = PropertyType;

    /**
     * Références des constantes utilisées dans le template.
     */
    public BOX_ID = FolderPropertyCodes.BOX_ID;

    /**
     * Références des composants utilisés pour filtrer un attribut.
     */
    @ViewChildren('filterChip') filterChips: QueryList<ElementRef> = new QueryList<ElementRef>();

    /**
     * Liste des définitions des colonnes affichées dans 'Mes dossiers'.
     */
    @Input() columns: ColumnDefinition[] = [];

    /**
     * Formulaire de recherche.
     */
    public searchForm: UntypedFormGroup;

    /**
     * Liste des attributs pouvant être filtrés (Ne contient que les attributs pour lequels aucun filtre n'est définit)*/
    public availableAttributesList: AttributeDefinition[] = [];

    /**
     * Liste des attributs pour lequels un filtre est définit
     */
    public filteredAttributesList: AttributeDefinition[] = [];

    /**
     * Gestionnaire de filtres associé au composant.
     */
    public currentFilterHandler: FoldersListFilterHandler;

    /**
     * Nombre de filtres appliqués.
     * (Utilisé pour déterminer l'ajout d'un nouveau filtre dans la liste des filtres)
     */
    private filtersCount: number = 0;

    /**
     * Filtre prédéfini actuellement appliqué à 'Mes dossiers'.
     */
    public currentPredefinedFilter: PredefinedFilter = new PredefinedFilter();

    /**
     * Indique si un filtre a été appliqué par l'utilisateur.
     * (Si la valeur est false cela indique qu'il s'agit d'un filtre prédéfini)
     */
    private isFilterAppliedByUser: boolean = false;

    /**
     * Id de l'utilisateur connecté.
     */
    public connectedUserId: string = null;

    /**
     * Constructeur.
     * @param formBuilder : Instance de FormBuilder utilisé pour créer le formulaire.
     * @param folderSearchStateService : Instance du service de gestion de l'état de la recherche des dossiers.
     * @param connectedUserStateService : Instance du service de gestion de l'état de l'utilisateur connecté.
     * @param cdr : Classe utilisé pour detecter les changements.
     */
    constructor(
        private formBuilder: UntypedFormBuilder,
        private folderSearchStateService: FolderSearchStateService,
        private connectedUserStateService: ConnectedUsersStateService,
        private cdr: ChangeDetectorRef
    ) {
        super();

        this.searchForm = this.formBuilder.group({
            searchControl: [''],
        });

        this.currentFilterHandler = new FoldersListFilterHandler(this);

        this.connectedUserId = this.connectedUserStateService.getUserConnected().userId;
    }

    /**
     * Appelé à l'initialisation du composant.
     */
    ngOnInit(): void {
        this.LoadSystemAttributes();
    }

    /**
     * Appelé après l'initialisation de la vue.
     */
    ngAfterViewInit(): void {
        this.subscribeToAppliedFiltersChange();
    }

    /**
     * Appelé à la destruction du composant.
     */
    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (this.currentFilterHandler) {
            this.currentFilterHandler.dispose();
        }
    }

    /**
     * Emis lorsque l'utilisateur clique sur le bouton 'Rechercher'.
     */
    public onSearch() {
        this.applyFilters();
    }

    /**
     * Emis lorsque l'utilisateur clique sur le bouton 'Réinitialiser'.
     */
    public onUndo() {
        this.reset();
    }

    /**
     * Appellée lorsque l'utilisateur ajoute un attribut à filtrer
     * @param attribute attribut à filtrer
     */
    public onAddAttributeToFilter(attribute: AttributeDefinition) {
        this.isFilterAppliedByUser = true;

        // Supprime de la liste des attributs l'attribut sélectionné
        this.availableAttributesList.splice(
            this.availableAttributesList.findIndex((a) => a.propertyCode === attribute.propertyCode),
            1
        );
        this.availableAttributesList.sort((a, b) => a.header.localeCompare(b.header));

        // Ajoute l'attribut sélectionné dans la liste des attributs sélectionnés
        this.filteredAttributesList.push(attribute);
    }

    /**
     * Appelée lorsque l'utilisateur retire un attribut du filtre.
     * @param attributefiltered Attribut à retirer du filtre.
     */
    public onRemoveAttributeFromFilter(attributefiltered: AttributeDefinition) {
        this.deleteFilterCriteria(attributefiltered);
    }

    /**
     * Applique les filtres à 'Mes dossiers'.
     * (Appellé par le gestionnaire de filtres FoldersListFilterHandler)
     */
    public applyFilters() {
        this.currentPredefinedFilter.searchTerm = this.getSearchTerm();
        this.currentPredefinedFilter.filteredAttributes = this.currentFilterHandler.filterValues;
        this.folderSearchStateService.setPredefinedFilterChange(this.currentPredefinedFilter);
    }

    /**
     * S'abonne aux evenements d'ajout ou de suppression de filtres.
     */
    private subscribeToAppliedFiltersChange() {
        this.filterChips.changes.pipe(takeUntil(this.componentDestroyed$)).subscribe(() => {
            if (this.filterChips.length > 0 && this.filterChips.length > this.filtersCount) {
                // Un nouveau filtre a été ajouté.
                this.filtersCount++;
                // Simule un Click sur le filtre dernièrement créé pour qu'il s'affiche en mode édition sauf s'il s'agit d'un filtre prédéfini.
                if (this.isFilterAppliedByUser === true) {
                    this.filterChips.last.nativeElement.click();
                }
            }
        });
    }

    /**
     * Charge les attributs système.
     */
    private LoadSystemAttributes() {
        this.attributeDefinitionsService.ListSystemAttributes().subscribe({
            next: (x) => {
                this.columns.forEach((c) => {
                    let index = x.findIndex((a) => a.propertyCode === c.propertyCode);

                    if (index > -1) {
                        this.availableAttributesList.push(x[index]);
                    }
                });

                this.initializeAttributeFilters();
            },
        });
    }

    /**
     * Supprime tous les critères de filtres appliqués à 'Mes dossiers'.
     */
    private deleteAllFilterCriterias() {
        // Supprime les attributs sélectionnés
        this.filteredAttributesList.forEach((x) => {
            // Ajoute l'attribut sélectionné dans la liste des attributs
            this.availableAttributesList.push(x);
        });

        // Reset le filtre
        if (this.currentFilterHandler) {
            this.currentFilterHandler.dispose();
            this.currentFilterHandler = new FoldersListFilterHandler(this);
            this.currentFilterHandler.initializeAttributeFilters(Object.assign([], this.availableAttributesList), null);
            this.applyFilters();
        }

        this.filteredAttributesList = [];
        this.availableAttributesList.sort((a, b) => a.header.localeCompare(b.header));

        // Met à jour le compteur de filtre
        this.filtersCount = 0;
    }

    /**
     * Supprime un critère de filtre appliqué à 'Mes dossiers'.
     * @param attribute : Attribut à retirer du filtre.
     */
    private deleteFilterCriteria(attribute: AttributeDefinition) {
        // Supprime de la liste des attributs sélectionnés l'attribut sélectionné
        this.filteredAttributesList.splice(
            this.filteredAttributesList.findIndex((a) => a.propertyCode === attribute.propertyCode),
            1
        );

        // Ajoute l'attribut sélectionné dans la liste des attributs
        this.availableAttributesList.push(attribute);
        this.availableAttributesList.sort((a, b) => a.header.localeCompare(b.header));

        // Reset le filtre
        if (this.currentFilterHandler) {
            this.currentFilterHandler.resetAttributeFilter(attribute.propertyCode);
        }

        // Met à jour le compteur de filtre
        this.filtersCount--;
    }

    /**
     * Réinitialise la recherche et les filtres.
     */
    private reset() {
        this.searchForm.reset();
        this.deleteAllFilterCriterias();
    }

    /**
     * Initialise les filtres.
     */
    private initializeAttributeFilters() {
        this.filteredAttributesList = [];
        this.cdr.detectChanges();

        this.availableAttributesList.sort((a, b) => a.header.localeCompare(b.header));

        if (this.currentFilterHandler) {
            this.currentFilterHandler.dispose();
        }

        this.currentFilterHandler = new FoldersListFilterHandler(this);

        this.currentFilterHandler.initializeAttributeFilters(Object.assign([], this.availableAttributesList), null);
    }

    /**
     * Retourne la valeur du champ 'Terme à rechercher'.
     * @returns La valeur du champ 'Terme à rechercher'.
     */
    private getSearchTerm() {
        let value = this.searchForm.controls['searchControl'].value;

        if (!!value) {
            return value;
        } else {
            return null;
        }
    }
}
